import type { Action } from '@reduxjs/toolkit'
import type { Observable } from 'rxjs'
import {
  EMPTY,
  auditTime,
  debounceTime,
  catchError,
  concat,
  concatMap,
  defer,
  filter,
  finalize,
  firstValueFrom,
  from,
  groupBy,
  ignoreElements,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs'
import type { Epic } from '../../index.ts'
import { orderWizardActions as wizard } from '../../Slice/OrderWizard/OrderWizardSlice.ts'
import { census, detect, diagnose, estimate, readRuns } from '../../../Services/OrderWizard/detectionClient.ts'
import { HEAD_FILES } from '../../../Services/OrderWizard/detectionCore.ts'
import { primerDiscovery, settledByMates } from '../../../Services/OrderWizard/discovery.ts'
import { placedGroups } from '../../../Services/OrderWizard/pools.ts'
import { suggestionPossible } from '../../../Services/OrderWizard/pools.ts'
import { dryRunSubmission, refusalOf } from '../../../Services/OrderWizard/submission.ts'
import { reviewSignature } from '../../../Services/OrderWizard/reviewSignature.ts'
import { submitChecksDue } from '../../../Services/OrderWizard/writeState.ts'
import { isArchiveOrder } from '../../../Services/OrderWizard/archive.ts'
import { FAILED_ESTIMATE, spellingHistory } from '../../../Services/OrderWizard/estimationCore.ts'
import { loadPrimerHistory, loadPrimerRegistry } from '../../../Services/OrderWizard/primerRegistryLoader.ts'
import { newlyValidated } from '../../../Services/OrderWizard/estimateValidation.ts'
import { primerRevision } from '../../../Services/OrderWizard/sheet/sheetMapping.ts'
import { matchSheet, sheetMatchOf } from '../../../Services/OrderWizard/sheet/sheetMatching.ts'
import { sheetPrimers } from '../../../Services/OrderWizard/sheet/sheetImport.ts'
import { trackPrimerEstimateOutcome } from '../../../Services/OrderWizard/telemetry.ts'
import { identifySheetPrimers, sheetPrimerPairs } from '../../../Services/OrderWizard/targetPlan.ts'
import { checkTarget, submitPrimers } from '../../../Services/OrderWizard/targetCheck.ts'
import { fileHandle, readsAtHand } from '../../../Services/OrderWizard/fileHandles.ts'
import { guarded } from './guarded.ts'
import { draftIdOf } from './draftIdOf.ts'
import { chunk } from '../../../Util/chunk.ts'
import type { RootState, WizardEpic } from './draftState.ts'
import { draftOf } from './draftState.ts'

/** The primer registry the read scan needs, fetched as the wizard starts rather than on the first drop. */
export const registryPrefetchEpic: Epic = (action$) =>
  action$.pipe(
    filter(wizard.restoreRequested.match),
    tap(() => void loadPrimerRegistry()),
    ignoreElements()
  )

type SheetChange = ReturnType<typeof wizard.sheetMappingChanged> | ReturnType<typeof wizard.sheetLoaded>

/**
 * A re-mapped primer column, or a sheet loaded unidentified (a pasted block), is identified against the
 * registry; until then the sheet settles no target.
 */
export const sheetPrimersEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter((action): action is SheetChange => wizard.sheetMappingChanged.match(action) || wizard.sheetLoaded.match(action)),
    withLatestFrom(state$),
    mergeMap(
      guarded('identifying the sheet primers', ([action, state]: [SheetChange, RootState]) => {
        const sheet = draftOf(state, action.payload.draftId)?.sheet
        if (!sheet || sheetPrimerPairs(sheet) !== undefined) {
          return EMPTY
        }
        return from(loadPrimerRegistry()).pipe(
          map((rows) =>
            wizard.sheetPrimersIdentified({
              draftId: action.payload.draftId,
              fileKey: sheet.fileKey,
              registeredPrimers: identifySheetPrimers(sheet, rows)
            })
          )
        )
      })
    )
  )

/**
 * A target the lab picks on Files is checked against the order's primers at once, with submit's own
 * validation, so a contradiction shows on Files rather than at submit. The latest choice wins.
 */
export const targetCheckEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(wizard.settingChanged.match),
    filter((action) => action.payload.change.setting === 'targetTaxonomy'),
    groupBy((action) => action.payload.draftId),
    mergeMap((perDraft) =>
      perDraft.pipe(
        switchMap(
          guarded('checking the target against the primers', (action: ReturnType<typeof wizard.settingChanged>) =>
            defer(() => firstValueFrom(state$)).pipe(
              mergeMap((state) => {
                const { draftId } = action.payload
                const draft = draftOf(state, draftId)
                const target = draft?.settings?.targetTaxonomy.value
                const primers = draft && submitPrimers(draft)
                if (!target || !primers) {
                  return of(wizard.targetChecked({ draftId }))
                }
                return from(checkTarget(target, primers)).pipe(map((check) => wizard.targetChecked({ draftId, check })))
              })
            )
          )
        )
      )
    )
  )

const CHECK_SETTLE_MS = 1500

/* the review's own steps, which would otherwise replace a refused submit's issues with a dry run's */
const CHECKS_IGNORE = [
  wizard.checksUpdated,
  wizard.stepChanged,
  wizard.reviewRequested,
  wizard.reviewLoaded,
  wizard.reviewFailed,
  wizard.platformOverrideRequested,
  wizard.platformOverridden,
  wizard.platformOverrideFailed,
  wizard.submitRequested,
  wizard.submitRefused
]

/**
 * The classic submit checks (dry run, nothing written) and the server's platform preview, run once the
 * draft has settled, so the issue list shows what submit would refuse before the lab presses it.
 */
export const submitChecksEpic: WizardEpic = (action$, state$, { OrderRepository, OrderSubmissionRepository }) =>
  action$.pipe(
    filter((action) => !CHECKS_IGNORE.some((ignored) => ignored.match(action))),
    map(draftIdOf),
    filter((draftId): draftId is string => !!draftId),
    groupBy((draftId) => draftId),
    mergeMap((perDraft) =>
      perDraft.pipe(
        debounceTime(CHECK_SETTLE_MS),
        switchMap(
          guarded('checking the order for submit', (draftId: string) =>
            defer(() => firstValueFrom(state$)).pipe(
              mergeMap((state) => {
                const draft = draftOf(state, draftId)
                const orderId = draft?.orderId
                if (!draft || !orderId || draft.submission || !submitChecksDue(draft, (group) => readsAtHand(draft.id, group))) {
                  return EMPTY
                }
                const signature = reviewSignature(draft)
                if (draft.checks?.signature === signature && !draft.checks.submitIssues && !draft.checks.submitMessages) {
                  return EMPTY
                }
                return from(
                  Promise.all([
                    firstValueFrom(OrderRepository.getOrder(orderId)),
                    firstValueFrom(OrderRepository.getAllSamplePools({ orderId })),
                    OrderSubmissionRepository.getSequencingPlatformPreview(orderId).catch(() => undefined)
                  ])
                ).pipe(
                  mergeMap(([order, pools, platform]) =>
                    from(dryRunSubmission(order, pools, state.authentication.user, undefined, draft.discovery?.readLengths, draft.primerChoice)).pipe(
                      map((readiness) =>
                        wizard.checksUpdated({
                          draftId,
                          checks: {
                            signature,
                            ...(readiness.ok ? {} : { refusal: refusalOf(readiness) }),
                            platformIssues: platform?.issues ?? []
                          }
                        })
                      )
                    )
                  )
                )
              })
            )
          )
        )
      )
    )
  )

const RUNS_BATCH = 16

/**
 * Where the file names could suggest pools, each forward file's sequencing run is read from its first
 * headers, so groups can be suggested per run. A file no longer at hand counts as run unknown.
 */
export const plateRunsEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(
      (action) => wizard.detectionCompleted.match(action) || wizard.filesReattached.match(action) || wizard.draftReconciled.match(action)
    ),
    map((action) => draftIdOf(action) as string),
    groupBy((draftId) => draftId),
    mergeMap((perDraft) =>
      perDraft.pipe(
        auditTime(200),
        concatMap(
          guarded('reading sequencing runs', (draftId: string) =>
            defer(() => firstValueFrom(state$)).pipe(
              mergeMap((state) => {
                const draft = draftOf(state, draftId)
                if (!draft || draft.poolsAnswered || !suggestionPossible(draft)) {
                  return EMPTY
                }
                const missing = Object.values(draft.groups).filter((group) => group.run === undefined)
                const unreadable = Object.fromEntries(
                  missing.filter((group) => !fileHandle(draft.id, group.forward)).map((group) => [group.key, ''])
                )
                const readable = missing.flatMap((group) => {
                  const blob = fileHandle(draft.id, group.forward)
                  return blob ? [{ key: group.key, blob }] : []
                })
                return concat(
                  Object.keys(unreadable).length > 0 ? of(wizard.runsRead({ draftId, runs: unreadable })) : EMPTY,
                  from(chunk(readable, RUNS_BATCH)).pipe(
                    concatMap((files) => from(readRuns({ files })).pipe(map((result) => wizard.runsRead({ draftId, runs: result.runs }))))
                  )
                )
              })
            )
          )
        )
      )
    )
  )

const ESTIMATE_BATCH = 8

const REDISCOVERY_TRIGGERS = [
  wizard.filesAdded,
  wizard.detectionCompleted,
  wizard.settingsWritten,
  wizard.reversePairingRequested,
  wizard.draftReconciled,
  wizard.filesReattached
]

/**
 * Reads that showed one primer only (R1 files without their R2) leave the pair open. Once samples hold
 * their R2 with both files at hand, the pair is identified on those pairs, as on a paired drop.
 */
export const primerRediscoveryEpic: Epic = (action$, state$) => {
  const tried = new Set<string>()
  return action$.pipe(
    filter((action) => REDISCOVERY_TRIGGERS.some((trigger) => trigger.match(action))),
    map((action) => draftIdOf(action)),
    filter((draftId): draftId is string => !!draftId),
    groupBy((draftId) => draftId),
    mergeMap((perDraft) =>
      perDraft.pipe(
        auditTime(200),
        concatMap(
          guarded('identifying the primers on the R2 files', (draftId: string) =>
            defer(() => firstValueFrom(state$)).pipe(
              mergeMap((state) => {
                const draft = draftOf(state, draftId)
                if (draft?.discovery?.primers.status !== 'partial') {
                  return EMPTY
                }
                const paired = placedGroups(draft)
                  .filter(
                    (group) => group.reverse && draft.files[group.forward] && draft.files[group.reverse] && readsAtHand(draft.id, group)
                  )
                  .sort((a, b) => draft.files[a.forward].name.localeCompare(draft.files[b.forward].name))
                  .slice(0, HEAD_FILES / 2)
                const attempt = `${draftId}\u0000${paired.map((group) => group.key).join('\u0000')}`
                if (paired.length === 0 || tried.has(attempt)) {
                  return EMPTY
                }
                tried.add(attempt)
                const files = paired.flatMap((group) =>
                  [group.forward, group.reverse as string].map((key) => ({
                    key,
                    name: draft.files[key].name,
                    size: draft.files[key].size,
                    platform: draft.files[key].platform ?? 'UNKNOWN',
                    blob: fileHandle(draft.id, key)
                  }))
                )
                return from(Promise.all([loadPrimerRegistry(), loadPrimerHistory()])).pipe(
                  mergeMap(([registryRows, history]) =>
                    registryRows
                      ? from(detect({ files, registryRows, classify: true })).pipe(map((result) => ({ result, registryRows, history })))
                      : EMPTY
                  ),
                  mergeMap(({ result, registryRows, history }) => {
                    const primers = result.guess && primerDiscovery(result.guess, registryRows, undefined, history)
                    return primers?.status === 'found'
                      ? of(wizard.primersRediscovered({ draftId, primers: settledByMates(draft.discovery?.primers, primers) }))
                      : EMPTY
                  })
                )
              })
            )
          )
        )
      )
    )
  )
}

/**
 * A batch's work with its ids marked as in flight for exactly as long as it runs: marked when it
 * starts, released however it ends, so a pass that fails before its batches run leaves nothing behind.
 */
const whileInFlight = <T extends { id: string }>(inFlight: Set<string>, batch: readonly T[], work: () => Observable<Action>) =>
  defer(() => {
    batch.forEach(({ id }) => inFlight.add(id))
    return work()
  }).pipe(finalize(() => batch.forEach(({ id }) => inFlight.delete(id))))

/**
 * Exact per-sample primers from each sample's reads, in the worker, a few samples per message so results
 * show up while the rest is still being read. Only for a single identified primer pair, and only for
 * samples whose files are at hand.
 */
export const estimatePrimersEpic: Epic = (action$, state$) => {
  const estimating = new Set<string>()
  return action$.pipe(
    filter(
      (action) =>
        wizard.detectionCompleted.match(action) || wizard.filesReattached.match(action) || wizard.primersRediscovered.match(action)
    ),
    groupBy((action) => action.payload.draftId),
    mergeMap((perDraft) =>
      perDraft.pipe(
        auditTime(200),
        concatMap(
          guarded('estimating primers', () =>
            defer(() => firstValueFrom(state$)).pipe(
              mergeMap((state) => {
                const draft = draftOf(state, perDraft.key)
                const primers = draft?.discovery?.primers
                if (!draft || primers?.status !== 'found') {
                  return EMPTY
                }
                const todo = Object.values(draft.groups).flatMap((group) => {
                  const forward = fileHandle(draft.id, group.forward)
                  const reverse = group.reverse ? fileHandle(draft.id, group.reverse) : undefined
                  const id = `${draft.id}\u0000${group.key}`
                  if (group.estimate || estimating.has(id) || !forward || (group.reverse && !reverse)) {
                    return []
                  }
                  return [{ key: group.key, forward, reverse, id }]
                })
                return from(loadPrimerHistory()).pipe(
                  map(spellingHistory),
                  mergeMap((history) =>
                    from(chunk(todo, ESTIMATE_BATCH)).pipe(
                      concatMap((batch) =>
                        whileInFlight(estimating, batch, () =>
                          from(
                            estimate({
                              pair: primers.pair,
                              groups: batch.map(({ key, forward, reverse }) => ({ key, forward, reverse })),
                              history
                            })
                          ).pipe(
                            map((result) => wizard.estimatesAdded({ draftId: draft.id, estimates: result.estimates })),
                            catchError((error: unknown) => {
                              console.warn('Order wizard: primer estimation failed', error)
                              return of(
                                wizard.estimatesAdded({
                                  draftId: draft.id,
                                  estimates: batch.map(({ key }) => ({ key, estimate: FAILED_ESTIMATE }))
                                })
                              )
                            })
                          )
                        )
                      )
                    )
                  )
                )
              })
            )
          )
        )
      )
    )
  )
}

/**
 * Where a sheet declares a sample's primers, how the estimator did is counted once per sample and sheet
 * revision, as outcome categories only, so its accuracy can be measured per lab in real use.
 */
export const validateEstimatesEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(
      (action) =>
        wizard.estimatesAdded.match(action) ||
        wizard.sheetLoaded.match(action) ||
        wizard.questionAnswered.match(action) ||
        wizard.sheetMappingChanged.match(action)
    ),
    withLatestFrom(state$),
    mergeMap(
      guarded('checking estimates', ([action, state]) => {
        const draft = draftOf(state, action.payload.draftId)
        const validated = draft ? newlyValidated(draft) : []
        if (!draft?.sheet || validated.length === 0) {
          return EMPTY
        }
        validated.forEach(({ outcome, spelledByLab }) => trackPrimerEstimateOutcome(outcome, spelledByLab ? 'lab' : 'registry'))
        return of(
          wizard.estimatesValidated({
            draftId: draft.id,
            keys: validated.map(({ group }) => group.key),
            revision: primerRevision(draft.sheet)
          })
        )
      })
    )
  )

/**
 * Where a sheet declares a sample's primers, its reads are checked against them in the worker: tag
 * mismatch, primers absent, one primer wrong, or another registered primer. Warnings, never blocks.
 * Not for multiplexed files, which carry every sample's tag by design.
 */
export const diagnosePrimersEpic: Epic = (action$, state$) => {
  const diagnosing = new Set<string>()
  return action$.pipe(
    filter(
      (action) =>
        wizard.sheetLoaded.match(action) ||
        wizard.questionAnswered.match(action) ||
        wizard.sheetMappingChanged.match(action) ||
        wizard.detectionCompleted.match(action) ||
        wizard.filesReattached.match(action)
    ),
    groupBy((action) => action.payload.draftId),
    mergeMap((perDraft) =>
      perDraft.pipe(
        auditTime(300),
        concatMap(
          guarded('diagnosing primers', () =>
            defer(() => firstValueFrom(state$)).pipe(
              mergeMap((state) => {
                const draft = draftOf(state, perDraft.key)
                const sheet = draft?.sheet
                if (!draft || !sheet || draft.settings?.sampleConfiguration.value === 'COMBINED_SAMPLE_PAIRS') {
                  return EMPTY
                }
                const revision = primerRevision(sheet)
                const match = sheetMatchOf(draft) ?? matchSheet(sheet, draft)
                const todo = Object.values(draft.groups).flatMap((group) => {
                  const rowIndex = match.byGroup[group.key]
                  const primers = rowIndex === undefined ? {} : sheetPrimers(sheet.rows[rowIndex], sheet.mapping)
                  const forward = fileHandle(draft.id, group.forward)
                  const reverse = group.reverse ? fileHandle(draft.id, group.reverse) : undefined
                  const id = `${draft.id}\u0000${group.key}\u0000${revision}`
                  if (
                    !primers.forward ||
                    !primers.reverse ||
                    group.diagnosedFor === revision ||
                    diagnosing.has(id) ||
                    !forward ||
                    (group.reverse && !reverse)
                  ) {
                    return []
                  }
                  return [{ key: group.key, forward, reverse, primers: { forward: primers.forward, reverse: primers.reverse }, id }]
                })
                if (todo.length === 0) {
                  return EMPTY
                }
                return from(loadPrimerRegistry()).pipe(
                  mergeMap((registryRows) =>
                    from(chunk(todo, ESTIMATE_BATCH)).pipe(
                      concatMap((batch) =>
                        whileInFlight(diagnosing, batch, () =>
                          from(
                            diagnose({
                              groups: batch.map(({ key, forward, reverse, primers }) => ({ key, forward, reverse, primers })),
                              registryRows
                            })
                          ).pipe(
                            map((result) => wizard.diagnosesAdded({ draftId: draft.id, revision, diagnoses: result.diagnoses })),
                            catchError((error: unknown) => {
                              console.warn('Order wizard: primer diagnosis failed', error)
                              return EMPTY
                            })
                          )
                        )
                      )
                    )
                  )
                )
              })
            )
          )
        )
      )
    )
  )
}

/** The tag pairs each archive's reads carry, in the worker, once per archive while its files are at hand. Shown only. */
export const archiveCensusEpic: Epic = (action$, state$) => {
  const censusing = new Set<string>()
  return action$.pipe(
    filter(
      (action) =>
        wizard.detectionCompleted.match(action) ||
        wizard.filesReattached.match(action) ||
        wizard.questionAnswered.match(action) ||
        wizard.primersRediscovered.match(action)
    ),
    groupBy((action) => action.payload.draftId),
    mergeMap((perDraft) =>
      perDraft.pipe(
        auditTime(200),
        concatMap(
          guarded('reading the archives’ tags', () =>
            defer(() => firstValueFrom(state$)).pipe(
              mergeMap((state) => {
                const draft = draftOf(state, perDraft.key)
                const primers = draft?.discovery?.primers
                if (!draft || primers?.status !== 'found' || !isArchiveOrder(draft)) {
                  return EMPTY
                }
                const archives = Object.values(draft.groups).flatMap((group) => {
                  const forward = fileHandle(draft.id, group.forward)
                  const reverse = group.reverse ? fileHandle(draft.id, group.reverse) : undefined
                  const id = `${draft.id}\u0000${group.key}`
                  if (group.archiveTags || !forward || !reverse || censusing.has(id)) {
                    return []
                  }
                  return [{ key: group.key, forward, reverse, id }]
                })
                if (archives.length === 0) {
                  return EMPTY
                }
                return whileInFlight(censusing, archives, () =>
                  from(
                    census({ pair: primers.pair, archives: archives.map(({ key, forward, reverse }) => ({ key, forward, reverse })) })
                  ).pipe(
                    map((result) => wizard.archiveTagsAdded({ draftId: draft.id, censuses: result.censuses })),
                    catchError((error: unknown) => {
                      console.warn('Order wizard: archive tag census failed', error)
                      return EMPTY
                    })
                  )
                )
              })
            )
          )
        )
      )
    )
  )
}
